import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateNominalAPR, calculateAdjustedAPR } from '@prolarus/shared';

interface InvoiceDetailsTabProps {
  invoice: {
    invoice_number: string | null;
    vendor_id: string | null;
    vendor_name?: string | null;
    po_number: string | null;
    // Dates & Payment Terms
    invoice_date: string | null;
    service_date: string | null;
    payment_terms: string | null;
    due_date: string | null;
    discount: number | null;
    discount_amt: number | null;
    discount_expiration: string | null;
    // Financial
    subtotal: number | null;
    tax_amount: number | null;
    total_amount: number | null;
    currency: string | null;
    // Notes
    notes: string | null;
  };
  vendorAddress?: {
    address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
  } | null;
  priceBook?: {
    id: string;
    name: string;
  } | null;
}

export function InvoiceDetailsTab({ invoice, vendorAddress, priceBook }: InvoiceDetailsTabProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'yyyy-MMM-dd');
    } catch {
      return '—';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return '—';
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = (value: number | null) => {
    if (value === null || value === undefined) return '—';
    return `${value}%`;
  };

  const formatAddress = () => {
    if (!vendorAddress) return null;
    const parts = [
      vendorAddress.address,
      vendorAddress.city,
      vendorAddress.state,
      vendorAddress.country,
    ].filter(Boolean);
    return parts.join(', ') || null;
  };

  const nominalAPR = calculateNominalAPR({
    discount: invoice.discount,
    dueDate: invoice.due_date,
    discountExpiration: invoice.discount_expiration,
  });

  const adjustedAPR = calculateAdjustedAPR({
    discount: invoice.discount,
    dueDate: invoice.due_date,
    discountExpiration: invoice.discount_expiration,
  });

  const ReadOnlyField = ({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={className}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm text-foreground">{value || '—'}</div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Dates & Payment Terms */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Dates & Payment Terms</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <ReadOnlyField label="Invoice Date" value={formatDate(invoice.invoice_date)} />
          <ReadOnlyField label="Service Date" value={formatDate(invoice.service_date)} />
          <ReadOnlyField label="Payment Terms" value={invoice.payment_terms} />
          <ReadOnlyField label="Due Date" value={formatDate(invoice.due_date)} />
          <ReadOnlyField label="Discount" value={formatPercent(invoice.discount)} />
          <ReadOnlyField label="Discount Amount" value={invoice.discount_amt !== null ? `${formatCurrency(invoice.discount_amt)} ${invoice.currency || 'USD'}` : null} />
          <ReadOnlyField label="Discount Expiration" value={formatDate(invoice.discount_expiration)} />
          <ReadOnlyField label="Nominal APR" value={nominalAPR?.formatted} />
          <ReadOnlyField label="Adjusted APR" value={adjustedAPR?.formatted} />
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <ReadOnlyField label="Subtotal" value={`${formatCurrency(invoice.subtotal)} ${invoice.currency || 'USD'}`} />
          <ReadOnlyField label="Tax Amount" value={`${formatCurrency(invoice.tax_amount)} ${invoice.currency || 'USD'}`} />
          <ReadOnlyField 
            label="Total Amount" 
            value={
              <span className="font-semibold">
                {formatCurrency(invoice.total_amount)} {invoice.currency || 'USD'}
              </span>
            } 
          />
          <ReadOnlyField label="Currency" value={invoice.currency || 'USD'} />
        </CardContent>
      </Card>

      {/* Notes */}
      {invoice.notes && (
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
